import { Language } from '@app/shared/models/app.model';
import { License } from '@app/modules/license/license.types';

/**
 * TEMPORARY: per-device translation-language restrictions.
 *
 * If the logged-in user owns a kit whose serial appears below, they only see
 * the languages in ALLOWED_LANGUAGE_CODES in the translation-language picker.
 * Any user without a matching device is unaffected and sees the full list.
 *
 * Serials come from the product API (`orderService.licenses`), on each
 * License's `serial` / `serials[]`.
 *
 * To remove this feature entirely: delete this file, and revert the
 * `restrictLanguagesForLicenses` call + `SpeakseeOrderService` injection in
 * `language-settings.service.ts`.
 *
 * Keep serials uppercase — matching is case-insensitive.
 */
const RESTRICTED_SERIALS: readonly string[] = [
  'U00121100500148',
  'U00123072500170',
  'U00121100500948',
  'U00121100500402',
];

const ALLOWED_LANGUAGE_CODES: readonly string[] = ['nl-NL', 'en-US'];

/** All serials attached to a license (`serial` plus every entry of `serials[]`). */
function serialsOf(license: License): string[] {
  const serials: string[] = [];
  if (license?.serial) serials.push(license.serial);
  if (license?.serials) serials.push(...license.serials);
  return serials;
}

/** True when any of the user's licenses is a restricted device. */
export function hasRestrictedSerial(
  licenses: License[] | null | undefined
): boolean {
  if (!licenses?.length) return false;
  const restricted = new Set(RESTRICTED_SERIALS.map((s) => s.toUpperCase()));
  return licenses.some((license) =>
    serialsOf(license).some((serial) => restricted.has(serial.toUpperCase()))
  );
}

/**
 * Filters `languages` down to the allowed set when the user owns a restricted
 * device. Otherwise the list is returned unchanged.
 */
export function restrictLanguagesForLicenses(
  licenses: License[] | null | undefined,
  languages: Language[]
): Language[] {
  if (!hasRestrictedSerial(licenses)) return languages;
  return languages.filter((lang) =>
    ALLOWED_LANGUAGE_CODES.includes(lang.languageCode)
  );
}
